//libraries imports
import { useEffect, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Chart } from "primereact/chart";

//Core Components
import Loader from "../../../core/common/loader";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../router/all_routes";
import CommonSelect from "../../../core/common/commonSelect";

//other components
import CategoryFilter from "./components/CategoryFilter";
import QuickInfoModal from "./components/QuickInfoModal";
import AssetActivityChart from "./components/AssetActivityChart";

// 🛠️ Helpers
import api from "../../../services/api";
import { showBasicAlert } from "../../../core/common/useAlert";
import { truncateCompanyText } from "../../../helper/company_helper";
import { toProperCase } from "../../../helper/textHelper";
import { humanReadableDate } from "../../../helper/dateTimeHelper.js";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { notify } from "../../../services/toast.config";
import { getMainStats } from "./helpers/statsCalculations";
import {
  calculateBrandCounts,
  canApplyCategoryFilter,
} from "./helpers/categoryHelper";
import { calculateAssetChanges } from "./helpers/assetChangesHelper";

// ===============================
// CONSTANTS & INITIAL STATE
// ===============================

const EMPTY_COMPANY = {
  company_name: "Select Company",
  company_id: "",
  company_type: "Type",
};

const AssetsStocks = () => {
  const signature = localStorage.getItem("signature");

  // ===============================
  // STATE MANAGEMENT
  // ===============================

  // Company & Filter States
  const [formDataCompanyChoose, setFormDataCompanyChoose] =
    useState(EMPTY_COMPANY);
  const [companies, setCompanies] = useState<any[]>([]);
  const [assetCategoryOptions, setAssetCategoryOptions] = useState<any[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string | any>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [modalInfo, setModalInfo] = useState("");
  const [assetsData, setAssetsData] = useState<any[]>([]);
  const [assetsHistoryData, setAssetsHistoryData] = useState<any[]>([]);
  const [modalData, setModalData] = useState<any[]>([]);
  const [modalDataColumns, setmodalDataColumns] = useState<any[]>([]);
  const [selectedHistoryAsset, setSelectedHistoryAsset] = useState<any | null>(
    null
  );
  const [categoryBreadcrumbPath, setCategoryBreadcrumbPath] = useState<any>("");

  // Chart Configuration States
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  // ===============================
  // DERIVED DATA
  // ===============================
  const companyOptions = companies?.map((company) => ({
    value: company.id.toString(),
    label: `${company.name} - ${company.type}`,
    companyName: company.name,
  }));

  // ===============================
  // HANDLER FUNCTIONS
  // ===============================

  // Company selection handler
  const handleCompanySelection = (selectedOption: any) => {
    if (!selectedOption) return;

    const newFormData = {
      company_id: selectedOption.value,
      company_name: selectedOption.label.split("-")[0]?.trim() || "",
      company_type: selectedOption.label.split("-")[1]?.trim() || "",
    };

    localStorage.setItem("selectedAssetCompany", JSON.stringify(newFormData));
    setFormDataCompanyChoose((prev: any) => ({ ...prev, ...newFormData }));
  };

  const handleChartCategorySelection = (
    clickedCategory: any,
    applyFilter: boolean
  ) => {
    if (
      applyFilter &&
      canApplyCategoryFilter(clickedCategory, assetCategoryOptions)
    ) {
      setCategoryFilter(clickedCategory);
    } else {
      handleCardClick("category-" + clickedCategory);
      const modalTrigger = document.createElement("button");
      modalTrigger.setAttribute("data-bs-toggle", "modal");
      modalTrigger.setAttribute("data-bs-target", "#asset-stats-info-modal");
      document.body.appendChild(modalTrigger);
      modalTrigger.click();
      document.body.removeChild(modalTrigger);
    }
  };

  const handleClearFilters = () => {
    setCategoryFilter(undefined);
    setCategoryBreadcrumbPath("");
  };

  const filteredAssets = useMemo(() => {
    let newFilteredData = [...assetsData];
    if (categoryFilter && categoryFilter !== "") {
      newFilteredData = newFilteredData.filter((item) => {
        return item?.category?.path?.some(
          (pathItem: any) =>
            pathItem?.id?.toString() === categoryFilter?.toString()
        );
      });
    }
    return newFilteredData;
  }, [assetsData, categoryFilter]);

  const assetChangesData = useMemo(() => {
    return calculateAssetChanges(filteredAssets, assetsHistoryData);
  }, [filteredAssets, assetsHistoryData]);

  // ===============================
  // API FUNCTIONS
  // ===============================

  // Fetch Company Data
  const getAllCompanies = async () => {
    try {
      const res = await api.post("userService/getAllCompanyList", {
        signature,
      });
      const companiesData = res?.data?.data?.data || [];
      setCompanies(companiesData);
    } catch (error) {
      showBasicAlert("Failed to load companies.");
    }
  };

  // Fetch Categories
  const fetchCategories = async () => {
    if (formDataCompanyChoose.company_id !== "") {
      try {
        const res = await api.post("userService/getCategory", {
          signature,
          company_id: formDataCompanyChoose.company_id,
          company_type: formDataCompanyChoose.company_type,
        });

        const categoriesData = res?.data?.data?.data || [];
        setAssetCategoryOptions(categoriesData);
      } catch (error: any) {
        notify.error(error?.data?.message?.[0]?.msg || "An error occurred.");
      }
    }
  };

  // Fetch Dashboard Statistics - Updated for new API structure
  const fetchDashboardData = async () => {
    if (formDataCompanyChoose.company_id !== "") {
      setIsLoading(true);
      try {
        const res = await api.post("assetService/dashboard-data", {
          signature,
          company_id: formDataCompanyChoose.company_id,
          company_type: formDataCompanyChoose.company_type,
        });

        const responseData = res?.data?.data?.data || {};
        setAssetsData(responseData?.assets);
        setAssetsHistoryData(responseData?.assetHistory);
      } catch (error: any) {
        notify.error(error?.data?.message?.[0]?.msg || "An error occurred.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Main data fetching function
  const fetchAllData = async () => {
    if (formDataCompanyChoose.company_id !== "") {
      try {
        setIsLoading(true);

        // Fetch data in sequence to ensure proper loading
        await fetchCategories();
        await fetchDashboardData();
      } catch (error) {
        console.error("Fetch all data error:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // ===============================
  // PERCENTAGE CALCULATIONS
  // ===============================
  const getActivePercentage = () => {
    const totalAssets = assetChangesData?.total || 0;
    if (assetChangesData.total === 0) return 0;
    return Number(((assetChangesData.active / totalAssets) * 100).toFixed(2));
  };

  const getInactivePercentage = () => {
    const totalAssets = assetChangesData?.total || 0;
    if (assetChangesData.total === 0) return 0;
    return Number(((assetChangesData.inactive / totalAssets) * 100).toFixed(2));
  };

  const getUpdatedPercentage = () => {
    const totalAssets = assetChangesData?.total || 0;
    if (assetChangesData.total === 0) return 0;
    return Number(((assetChangesData.updated / totalAssets) * 100).toFixed(2));
  };

  // ===============================
  // USE EFFECTS
  // ===============================

  // Initialize companies
  useEffect(() => {
    getAllCompanies();
    setCategoryFilter(undefined);
  }, []);

  // Handle company selection
  useEffect(() => {
    if (companies.length) {
      const storedCompany = localStorage.getItem("selectedAssetCompany");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        handleCompanySelection({
          value: parsed.company_id,
          label: `${parsed.company_name} - ${parsed.company_type}`,
        });
      } else {
        handleCompanySelection({
          value: companies[0].id.toString(),
          label: companies[0].name + " - " + companies[0].type,
        });
      }
    }
  }, [companies]);

  // Update UI when company changes
  useEffect(() => {
    if (formDataCompanyChoose.company_id) {
      fetchAllData();
      setCategoryFilter(undefined);
    }
  }, [formDataCompanyChoose]);

  // Initialize asset changes chart data - Updated for monthly data
  useEffect(() => {
    const chartDataConfig = {
      labels: ["Assigned", "Unassigned", "Updated"],
      datasets: [
        {
          label: "Asset Changes (This Month)",
          data: [
            assetChangesData?.active,
            assetChangesData?.inactive,
            assetChangesData?.updated,
          ],
          backgroundColor: ["#03C95A", "#FFC107", "#E70D0D"],
          borderWidth: 5,
          borderRadius: 10,
          borderColor: "#fff",
          hoverBorderWidth: 0,
          cutout: "60%",
        },
      ],
    };

    const chartOptionsConfig = {
      rotation: -100,
      circumference: 200,
      layout: {
        padding: { top: -20, bottom: -20 },
      },
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: function (context: any) {
              const label = context.label || "";
              const value = context.parsed || 0;
              const percentage =
                assetChangesData.total > 0
                  ? Number(((value / assetChangesData.total) * 100).toFixed(2))
                  : 0;
              return `  ${label}: ${value} (${percentage}%)`;
            },
          },
        },
      },
      onClick: (event: any, elements: any, chart: any) => {
        if (elements.length > 0) {
          const { index } = elements[0];
          const legendName = chart.data.labels?.[index]; // e.g. "Assigned"
          handleCardClick(`assets-changes-${legendName.toLowerCase()}`);
          const modalTrigger = document.createElement("button");
          modalTrigger.setAttribute("data-bs-toggle", "modal");
          modalTrigger.setAttribute(
            "data-bs-target",
            "#asset-stats-info-modal"
          );
          document.body.appendChild(modalTrigger);
          modalTrigger.click();
          document.body.removeChild(modalTrigger);
        }
      },
    };

    setChartData(chartDataConfig);
    setChartOptions(chartOptionsConfig);
  }, [assetChangesData]);

  //Table Columns
  const total_assets_columns = [
    {
      title: "Asset Name",
      dataIndex: "product_name",
      render: (text: string, record?: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-lg avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.product_image?.url ||
                  record?.product_image?.url === "null"
                  ? "assets/img/products/product-icon.png"
                  : record?.product_image?.url
              }
              alt="img"
            />
          </li>
          <div className="ms-2">
            <h6 className="fs-12 fw-medium mb-1 table-text-wrap">{text}</h6>
            <span className="fs-12 fw-normal table-text-wrap">
              {record?.product_model}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => {
        const nameCompare = (a?.product_name || "").localeCompare(
          b?.product_name || "",
          "en",
          { sensitivity: "base", numeric: true }
        );

        return nameCompare !== 0
          ? nameCompare
          : (a?.product_model || "").localeCompare(
            b?.product_model || "",
            "en",
            { sensitivity: "base", numeric: true }
          );
      },
    },
    {
      title: "Serial Number",
      dataIndex: "serial_no",
      render: (text: string) => (
        <h6 className="fs-12 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2 table-text-wrap">
          {text.toUpperCase()}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.serial_no || "").localeCompare(b?.serial_no || ""),
    },
    {
      title: "Brand",
      dataIndex: "product_brand",
      render: (text: any) => (
        <span className="badge d-inline-flex align-items-center justify-content-center badge-sm badge-secondary-transparent table-text-wrap">
          {text?.toUpperCase()}
        </span>
      ),
      sorter: (a: any, b: any) =>
        (a?.product_brand || "").localeCompare(b?.product_brand || ""),
    },
    {
      title: "Category",
      dataIndex: "asset_category_id",
      render: (text: string | number, record: any) => (
        <span
          className="badge d-inline-flex align-items-center justify-content-center badge-sm badge-info-transparent table-text-wrap"
          style={{ minWidth: "150px" }}
        >
          {record?.category?.breadcrumb}
        </span>
      ),
    },
    {
      title: "Assignment Type",
      dataIndex: "assigned_to_type",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium table-text-wrap">
          {text == null ? "N/A" : toProperCase(text.replace("_", " "))}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.assigned_to_type || "").localeCompare(b?.assigned_to_type || ""),
    },
    {
      title: "Asset Status",
      dataIndex: "product_status",
      render: (text: any, record: any) => {
        const displayText =
          record?.overall_status === "lost" ? "Lost" : text;
        return (
          <span
            className={`badge d-inline-flex align-items-center badge-xs ${displayText === "Active"
              ? "badge-success"
              : displayText === "Inactive"
                ? "badge-danger"
                : "badge-warning"
              }`}
            title={
              record?.overall_status === "lost"
                ? record?.lost_remark || ""
                : ""
            }
          >
            <i className="ti ti-point-filled me-1"></i>
            {displayText}
          </span>
        );
      },
      sorter: (a: any, b: any) =>
        a?.product_status?.length - b?.product_status?.length,
    },
  ];

  const lost_assets_columns = [
    ...total_assets_columns.filter(
      (column: any) => column.title !== "Asset Status"
    ),
    {
      title: "Lost Remark",
      dataIndex: "lost_remark",
      render: (text: string) => (
        <span
          className="fs-12 table-text-wrap"
          style={{ maxWidth: "220px", display: "inline-block" }}
          title={text || ""}
        >
          {text || "N/A"}
        </span>
      ),
      sorter: (a: any, b: any) =>
        (a?.lost_remark || "").localeCompare(b?.lost_remark || ""),
    },
    {
      title: "Lost Date",
      dataIndex: "lost_date",
      render: (text: string) =>
        text ? (
          <span className="badge d-inline-flex align-items-center badge-sm badge-soft-warning">
            {humanReadableDate(text)}
          </span>
        ) : (
          <span className="fs-12">N/A</span>
        ),
      sorter: (a: any, b: any) =>
        new Date(a?.lost_date || 0).getTime() -
        new Date(b?.lost_date || 0).getTime(),
    },
  ];

  const asset_columns = [
    {
      title: "Asset Name",
      dataIndex: "product_name",
      render: (text: string, record?: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-lg avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.product_image?.url ||
                  record?.product_image?.url === "null"
                  ? "assets/img/products/product-icon.png"
                  : record?.product_image?.url
              }
              alt="img"
            />
          </li>
          <div className="ms-2">
            <h6 className="fs-12 fw-medium mb-1 table-text-wrap">{text}</h6>
            <span className="fs-12 fw-normal table-text-wrap">
              {record?.product_model}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => {
        const nameCompare = (a?.product_name || "").localeCompare(
          b?.product_name || "",
          "en",
          { sensitivity: "base", numeric: true }
        );

        return nameCompare !== 0
          ? nameCompare
          : (a?.product_model || "").localeCompare(
            b?.product_model || "",
            "en",
            { sensitivity: "base", numeric: true }
          );
      },
    },
    {
      title: "Serial Number",
      dataIndex: "serial_no",
      render: (text: string) => (
        <h6 className="fs-12 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2 table-text-wrap">
          {text.toUpperCase()}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.serial_no || "").localeCompare(b?.serial_no || ""),
    },
    {
      title: "Assignment Type",
      dataIndex: "assigned_to_type",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium table-text-wrap">
          {text == null ? "N/A" : toProperCase(text.replace("_", " "))}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.assigned_to_type || "").localeCompare(b?.assigned_to_type || ""),
    },
    {
      title: "Assigned To",
      dataIndex: "assigned_to_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <div className="">
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">
              {toProperCase(text)}
            </h6>
            {record.assigned_to_type == "user" && (
              <span className="fs-12 fw-normal table-text-wrap">
                {toProperCase(record?.assigned_to_designation || "NA")}
              </span>
            )}
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.assigned_to_name || "").localeCompare(b?.assigned_to_name || ""),
    },
    {
      title: "Assigned By",
      dataIndex: "assigned_by_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-sm avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.assigned_by_logo || record?.assigned_by_logo === "null"
                  ? "assets/img/users/user.png"
                  : record?.assigned_by_logo
              }
              alt="img"
            />
          </li>

          <div>
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">
              {toProperCase(text)}
            </h6>

            <span className="fs-12 fw-normal table-text-wrap">
              {toProperCase(record?.assigned_by_designation || "NA")}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.assigned_by_name || "").localeCompare(b?.assigned_by_name || ""),
    },
    {
      title: "Category",
      dataIndex: "asset_category_id",
      render: (text: string | number, record: any) => (
        <span
          className="badge d-inline-flex align-items-center justify-content-center badge-sm badge-info-transparent table-text-wrap"
          style={{ minWidth: "150px" }}
        >
          {record?.category?.breadcrumb}
        </span>
      ),
    },
  ];

  const user_columns = [
    {
      title: "User Name",
      dataIndex: "assigned_to_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-md avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.assigned_to_logo || record?.assigned_to_logo === "null"
                  ? "assets/img/users/user.png"
                  : record?.assigned_to_logo
              }
              alt="img"
            />
          </li>
          <div className="">
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">
              {toProperCase(text)}
            </h6>
            {record.assigned_to_type == "user" && (
              <span className="fs-12 fw-normal table-text-wrap">
                {toProperCase(record?.assigned_to_designation || "NA")}
              </span>
            )}
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.assigned_to_name || "").localeCompare(b?.assigned_to_name || ""),
    },
    {
      title: "Asset Name",
      dataIndex: "product_name",
      render: (text: string, record?: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-lg avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.product_image?.url ||
                  record?.product_image?.url === "null"
                  ? "assets/img/products/product-icon.png"
                  : record?.product_image?.url
              }
              alt="img"
            />
          </li>
          <div className="ms-2">
            <h6 className="fs-12 fw-medium mb-1 table-text-wrap">
              {toProperCase(text)}
            </h6>
            <span className="fs-12 fw-normal table-text-wrap">
              {toProperCase(record?.product_model)}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => {
        const nameCompare = (a?.product_name || "").localeCompare(
          b?.product_name || "",
          "en",
          { sensitivity: "base", numeric: true }
        );

        return nameCompare !== 0
          ? nameCompare
          : (a?.product_model || "").localeCompare(
            b?.product_model || "",
            "en",
            { sensitivity: "base", numeric: true }
          );
      },
    },
    {
      title: "Serial Number",
      dataIndex: "serial_no",
      render: (text: string) => (
        <h6 className="fs-12 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2 table-text-wrap">
          {text.toUpperCase()}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.serial_no || "").localeCompare(b?.serial_no || ""),
    },
    {
      title: "Assigned By",
      dataIndex: "assigned_by_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-sm avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.assigned_by_logo || record?.assigned_by_logo === "null"
                  ? "assets/img/users/user.png"
                  : record?.assigned_by_logo
              }
              alt="img"
            />
          </li>

          <div>
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">
              {toProperCase(text)}
            </h6>

            <span className="fs-12 fw-normal table-text-wrap">
              {toProperCase(record?.assigned_by_designation || "NA")}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.assigned_by_name || "").localeCompare(b?.assigned_by_name || ""),
    },
  ];

  const department_columns = [
    {
      title: "Department Name",
      dataIndex: "assigned_to_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <div className="">
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">
              {toProperCase(text)}
            </h6>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.assigned_to_name || "").localeCompare(b?.assigned_to_name || ""),
    },
    {
      title: "Asset Name",
      dataIndex: "product_name",
      render: (text: string, record?: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-lg avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.product_image?.url ||
                  record?.product_image?.url === "null"
                  ? "assets/img/products/product-icon.png"
                  : record?.product_image?.url
              }
              alt="img"
            />
          </li>
          <div className="ms-2">
            <h6 className="fs-12 fw-medium mb-1 table-text-wrap">
              {toProperCase(text)}
            </h6>
            <span className="fs-12 fw-normal table-text-wrap">
              {toProperCase(record?.product_model)}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => {
        const nameCompare = (a?.product_name || "").localeCompare(
          b?.product_name || "",
          "en",
          { sensitivity: "base", numeric: true }
        );

        return nameCompare !== 0
          ? nameCompare
          : (a?.product_model || "").localeCompare(
            b?.product_model || "",
            "en",
            { sensitivity: "base", numeric: true }
          );
      },
    },
    {
      title: "Serial Number",
      dataIndex: "serial_no",
      render: (text: string) => (
        <h6 className="fs-12 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2 table-text-wrap">
          {text.toUpperCase()}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.serial_no || "").localeCompare(b?.serial_no || ""),
    },
    {
      title: "Assigned By",
      dataIndex: "assigned_by_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-sm avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.assigned_by_logo || record?.assigned_by_logo === "null"
                  ? "assets/img/users/user.png"
                  : record?.assigned_by_logo
              }
              alt="img"
            />
          </li>

          <div>
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">
              {toProperCase(text)}
            </h6>

            <span className="fs-12 fw-normal table-text-wrap">
              {toProperCase(record?.assigned_by_designation || "NA")}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.assigned_by_name || "").localeCompare(b?.assigned_by_name || ""),
    },
  ];

  const location_columns = [
    {
      title: "Location Name",
      dataIndex: "assigned_to_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <h6 className="fs-13 fw-medium mb-1 table-text-wrap">
            {toProperCase(text)}
          </h6>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.assigned_to_name || "").localeCompare(b?.assigned_to_name || ""),
    },
    {
      title: "Address",
      dataIndex: "assigned_to_name",
      render: (text: string, record: any) => {
        const building = record?.building
          ? toProperCase(record.building)
          : "NA";
        const floor = record?.floor ? toProperCase(record.floor) : "NA";
        const room = record?.room ? toProperCase(record.room) : "NA";

        return (
          <div className="d-flex align-items-center file-name-icon">
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">
              {`${building}, ${floor}, ${room}`}
            </h6>
          </div>
        );
      },
    },
    {
      title: "Asset Name",
      dataIndex: "product_name",
      render: (text: string, record?: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-lg avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.product_image?.url ||
                  record?.product_image?.url === "null"
                  ? "assets/img/products/product-icon.png"
                  : record?.product_image?.url
              }
              alt="img"
            />
          </li>
          <div className="ms-2">
            <h6 className="fs-12 fw-medium mb-1 table-text-wrap">
              {toProperCase(text)}
            </h6>
            <span className="fs-12 fw-normal table-text-wrap">
              {toProperCase(record?.product_model)}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => {
        const nameCompare = (a?.product_name || "").localeCompare(
          b?.product_name || "",
          "en",
          { sensitivity: "base", numeric: true }
        );

        return nameCompare !== 0
          ? nameCompare
          : (a?.product_model || "").localeCompare(
            b?.product_model || "",
            "en",
            { sensitivity: "base", numeric: true }
          );
      },
    },
    {
      title: "Serial Number",
      dataIndex: "serial_no",
      render: (text: string) => (
        <h6 className="fs-12 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2 table-text-wrap">
          {text.toUpperCase()}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.serial_no || "").localeCompare(b?.serial_no || ""),
    },
    {
      title: "Assigned By",
      dataIndex: "assigned_by_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-sm avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.assigned_by_logo || record?.assigned_by_logo === "null"
                  ? "assets/img/users/user.png"
                  : record?.assigned_by_logo
              }
              alt="img"
            />
          </li>

          <div>
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">
              {toProperCase(text)}
            </h6>

            <span className="fs-12 fw-normal table-text-wrap">
              {toProperCase(record?.assigned_by_designation || "NA")}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.assigned_by_name || "").localeCompare(b?.assigned_by_name || ""),
    },
  ];

  const asset_changes_columns = [
    {
      title: "Asset Name",
      dataIndex: "product_name",
      render: (text: string, record?: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-lg avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.product_image?.url ||
                  record?.product_image?.url === "null"
                  ? "assets/img/products/product-icon.png"
                  : record?.product_image?.url
              }
              alt="img"
            />
          </li>
          <div className="ms-2">
            <h6 className="fs-12 fw-medium mb-1 table-text-wrap">{text}</h6>
            <span className="fs-12 fw-normal table-text-wrap">
              {record?.product_model}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => {
        const nameCompare = (a?.product_name || "").localeCompare(
          b?.product_name || "",
          "en",
          { sensitivity: "base", numeric: true }
        );

        return nameCompare !== 0
          ? nameCompare
          : (a?.product_model || "").localeCompare(
            b?.product_model || "",
            "en",
            { sensitivity: "base", numeric: true }
          );
      },
    },
    {
      title: "Brand",
      dataIndex: "product_brand",
      render: (text: any) => (
        <span className={`badge badge-sm badge-secondary-transparent `}>
          {text}
        </span>
      ),
      sorter: (a: any, b: any) => a?.status?.length - b?.status?.length,
    },
    {
      title: "Serial Number",
      dataIndex: "serial_no",
      render: (text: string) => (
        <h6 className="fs-12 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2 table-text-wrap">
          {text.toUpperCase()}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.serial_no || "").localeCompare(b?.serial_no || ""),
    },
    {
      title: "Change Type",
      dataIndex: "action_type",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium table-text-wrap">
          {text == null ? "N/A" : toProperCase(text.replace("_", " "))}
        </h6>
      ),
    },
    {
      title: "View History",
      dataIndex: "total_changes",
      render: (text: any, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <Link
            to="#"
            className="me-2"
            title="Show Details"
            data-bs-toggle="modal"
            data-bs-target="#asset-history-modal"
            data-inert={true}
            onClick={() =>
              setSelectedHistoryAsset({ ...record, id: record?.asset_id })
            }
          >
            <div className="">
              <h6 className="fs-13 fw-medium mb-1 badge badge-sm badge-info">
                View ({text}) changes
              </h6>
            </div>
          </Link>
        </div>
      ),
      sorter: (a: any, b: any) => a?.total_changes - b?.total_changes,
    },
  ];

  const brand_columns = [
    {
      title: "Brand Name",
      dataIndex: "name",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-info-transparent py-2 p-2 table-text-wrap">
          {toProperCase(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => (a?.name || "").localeCompare(b?.name || ""),
    },
    {
      title: "Total Assets",
      dataIndex: "count",
      render: (text: string | number) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.count - b?.count,
    },
  ];

  // Handler
  const handleCardClick = (category: string) => {
    let title = "";
    let filteredData: any[] = filteredAssets;
    let columns: any = asset_columns;

    let categoryFilteredHistoryData = categoryFilter
      ? assetsHistoryData.filter((item) => {
        return item?.category?.path?.some(
          (pathItem: any) =>
            pathItem?.id?.toString() == categoryFilter?.toString()
        );
      })
      : assetsHistoryData;

    switch (category) {
      case "total-assets":
        title = "All Assets List";
        columns = total_assets_columns;
        break;

      case "assigned-assets":
        title = "Assigned Assets List";
        filteredData = filteredAssets.filter(
          (i) =>
            i.overall_status !== "lost" &&
            i.assigned_to_type !== "not_assigned" &&
            i.assigned_to_type !== "not_created"
        );
        break;

      case "instock-assets":
        title = "In Stock Assets List";
        filteredData = filteredAssets.filter(
          (i) =>
            i.overall_status !== "lost" &&
            (i.assigned_to == null || i.assigned_to_type == "not_created") &&
            i.product_status == "Active"
        );
        columns = total_assets_columns;
        break;

      case "lost-assets":
        title = "Lost Assets List";
        filteredData = filteredAssets.filter(
          (i) => i.overall_status === "lost"
        );
        columns = lost_assets_columns;
        break;

      case "inactive-assets":
        title = "Inactive Assets List";
        filteredData = filteredAssets.filter(
          (i) =>
            i.overall_status !== "lost" &&
            (i.assigned_to == null || i.assigned_to_type == "not_created") &&
            i.product_status == "Inactive"
        );
        columns = total_assets_columns;
        break;

      case "user-assignments":
        title = "Users Assets List";
        filteredData = filteredAssets.filter(
          (i) => i.assigned_to_type === "user" && i.overall_status !== "lost"
        );
        columns = user_columns;
        break;

      case "location-assignments":
        title = "Location Assets List";
        filteredData = filteredAssets.filter(
          (i) =>
            i.assigned_to_type === "location" && i.overall_status !== "lost"
        );
        columns = location_columns;
        break;

      case "department-assignments":
        title = "Departments Assets List";
        filteredData = filteredAssets.filter(
          (i) =>
            i.assigned_to_type === "department" && i.overall_status !== "lost"
        );
        columns = department_columns;
        break;

      case "assets-changes-assigned":
        title = "Assigned Assets List";

        filteredData = categoryFilteredHistoryData?.filter(
          (data) => data.changes_type == "assigned"
        );
        columns = asset_changes_columns;
        break;

      case "assets-changes-unassigned":
        title = "Unassigned Assets List";

        filteredData = categoryFilteredHistoryData?.filter(
          (data) => data.changes_type == "unassigned"
        );
        columns = asset_changes_columns;
        break;

      case "assets-changes-updated":
        title = "Updated Assets List";
        filteredData = categoryFilteredHistoryData?.filter(
          (data) => data.changes_type == "updated"
        );
        columns = asset_changes_columns;
        break;

      default:
        if (category.startsWith("category-")) {
          const categoryId = category.split("-")[1];
          let newFilteredData = [...filteredAssets];
          newFilteredData = newFilteredData.filter((item) => {
            return item?.category?.path?.some(
              (pathItem: any) =>
                pathItem.id.toString() === categoryId.toString()
            );
          });

          const brandsData = calculateBrandCounts(newFilteredData);
          filteredData = brandsData || [];
          title = "Products Brand List";
          columns = brand_columns;
        }
        break;
    }

    setModalInfo(title);
    setModalData(filteredData);
    setmodalDataColumns(columns);
  };

  const mainStats = getMainStats(filteredAssets);

  // ===============================
  // RENDER COMPONENT
  // ===============================

  return (
    <>
      {isLoading && <Loader />}

      <div className="page-wrapper">
        <div className="content">
          {/* Header Section */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Asset Dashboard</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Administration</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Asset Dashboard
                  </li>
                </ol>
              </nav>
            </div>

            <div
              className="d-flex my-xl-auto right-content align-items-center flex-wrap"
              title="Select Company"
            >
              <CommonSelect
                className="select mb-2 me-2"
                options={companyOptions}
                defaultValue={{
                  value: formDataCompanyChoose.company_id,
                  label:
                    truncateCompanyText(
                      formDataCompanyChoose.company_name,
                      20
                    ) +
                    "-" +
                    formDataCompanyChoose.company_type,
                }}
                onChange={handleCompanySelection}
              />

              {/* Quick Actions Dropdown */}
              <div className="me-2 mb-2" title="Quick Actions Menu">
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-primary d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-bolt me-2"></i>
                    Quick Actions
                  </Link>

                  <ul className="dropdown-menu p-3">
                    <li>
                      <Link
                        to={all_routes.assignAssets}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-box-multiple me-2" />
                        Assets Overview
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={all_routes.assetProducts || "#"}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-package me-2" />
                        Products Overview
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={all_routes.assetCategories || "#"}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-category me-2" />
                        Categories Overview
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={all_routes.assetLocations || "#"}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-map-pin me-2" />
                        Locations Overview
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>

          <div className="row g-5">
            <div className="col-lg-4 col-sm-12">
              <div className="row d-flex">
                <div className="card flex-fill" style={{ zIndex: 99 }}>
                  <div className="card-body py-3 bg-white">
                    <h5 className="text-center text-muted fs-16 mb-3">
                      <i className="ti ti-filter me-2 fs-16"></i>
                      Filter Dashboard By Category
                    </h5>

                    <CategoryFilter
                      assetCategoryOptions={assetCategoryOptions}
                      categoryFilter={categoryFilter}
                      setCategoryFilter={setCategoryFilter}
                      setCategoryBreadcrumbPath={setCategoryBreadcrumbPath}
                      onClearFilter={handleClearFilters}
                      categoryBreadcrumbPath={categoryBreadcrumbPath}
                    />
                  </div>
                </div>
              </div>
              <div className="row d-flex">
                <div className="card">
                  <div className="card-header pb-3 d-flex align-items-center justify-content-between flex-wrap">
                    <h5 className="mb-2 d-flex align-items-center">
                      <i className="ti ti-user-check text-primary me-2"></i>
                      Asset Assignments
                    </h5>
                  </div>
                  <div className="card-body bg-white">
                    {/* Assignment Breakdown */}
                    <div className="assignment-breakdown">
                      {/* Users */}
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#asset-stats-info-modal"
                        data-inert={true}
                        onClick={() => handleCardClick("user-assignments")}
                      >
                        <div className="d-flex align-items-center justify-content-between mb-3 p-2 rounded-3 bg-light-primary">
                          <div className="d-flex align-items-center">
                            <div className="avatar avatar-sm bg-primary text-white rounded-circle me-3">
                              <i className="ti ti-user fs-14"></i>
                            </div>
                            <div>
                              <p className="mb-0 fw-medium">Users</p>
                              <small className="text-muted">
                                Individual assignments
                              </small>
                            </div>
                          </div>
                          <div className="text-end">
                            <h5 className="mb-0 text-primary">
                              {mainStats?.assignmentBreakdown?.users}
                            </h5>
                            <small className="text-muted">
                              {mainStats?.assignmentBreakdown?.userPercentage}%
                            </small>
                          </div>
                        </div>
                      </Link>

                      {/* Locations */}
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#asset-stats-info-modal"
                        data-inert={true}
                        onClick={() => handleCardClick("location-assignments")}
                      >
                        <div className="d-flex align-items-center justify-content-between mb-3 p-2 rounded-3 bg-light-success">
                          <div className="d-flex align-items-center">
                            <div className="avatar avatar-sm bg-success text-white rounded-circle me-3">
                              <i className="ti ti-map-pin fs-14"></i>
                            </div>
                            <div>
                              <p className="mb-0 fw-medium">Locations</p>
                              <small className="text-muted">
                                Physical locations
                              </small>
                            </div>
                          </div>
                          <div className="text-end">
                            <h5 className="mb-0 text-success">
                              {mainStats?.assignmentBreakdown?.locations}
                            </h5>
                            <small className="text-muted">
                              {
                                mainStats?.assignmentBreakdown
                                  ?.locationPercentage
                              }
                              %
                            </small>
                          </div>
                        </div>
                      </Link>

                      {/* Departments */}
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#asset-stats-info-modal"
                        data-inert={true}
                        onClick={() =>
                          handleCardClick("department-assignments")
                        }
                      >
                        <div className="d-flex align-items-center justify-content-between mb-3 p-2 rounded-3 bg-light-warning">
                          <div className="d-flex align-items-center">
                            <div className="avatar avatar-sm bg-warning text-white rounded-circle me-3">
                              <i className="ti ti-building fs-14"></i>
                            </div>
                            <div>
                              <p className="mb-0 fw-medium">Departments</p>
                              <small className="text-muted">
                                Department assignments
                              </small>
                            </div>
                          </div>
                          <div className="text-end">
                            <h5 className="mb-0 text-warning">
                              {mainStats?.assignmentBreakdown?.departments}
                            </h5>
                            <small className="text-muted">
                              {
                                mainStats?.assignmentBreakdown
                                  ?.departmentPercentage
                              }
                              %
                            </small>
                          </div>
                        </div>
                      </Link>
                    </div>

                    {/* Summary */}
                    <div className="border-top p-3 mt-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fs-13 text-muted">
                          Assignment Rate
                        </span>
                        <span className="fs-13 fw-medium text-primary">
                          {mainStats?.total > 0
                            ? Number(
                              (
                                (mainStats?.assignmentBreakdown?.total /
                                  mainStats?.total) *
                                100
                              ).toFixed(2)
                            )
                            : 0}
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-8 col-sm-12">
              {/* Stats Cards Section */}
              <div className="row gap-0">
                {/* Total Assets */}
                <div className="col-xl-3 col-md-6 px-2">
                  <div className="card">
                    <div className="card-body bg-white">
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#asset-stats-info-modal"
                        onClick={() => handleCardClick("total-assets")}
                      >
                        <div className="border-bottom mb-3 pb-3 d-flex align-items-center justify-content-between">
                          <div>
                            <h2 className="mb-2">{mainStats?.total}</h2>

                            <p className="fw-medium text-truncate">
                              Total Assets
                            </p>
                          </div>
                          <span className="mb-2 text-info">
                            <i className="ti ti-box-multiple fs-30" />
                          </span>
                        </div>
                      </Link>

                      <div className="d-flex">
                        <p className="fs-12 fw-normal d-flex align-items-center text-info">
                          List of all the Products
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Assigned Assets */}
                <div className="col-xl-3 col-md-6 px-2">
                  <div className="card">
                    <div className="card-body bg-white">
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#asset-stats-info-modal"
                        onClick={() => handleCardClick("assigned-assets")}
                      >
                        <div className="border-bottom mb-3 pb-3 d-flex align-items-center justify-content-between">
                          <div>
                            <h2 className="mb-2">{mainStats?.assigned}</h2>
                            <p className="fw-medium text-truncate">
                              Assigned Assets
                            </p>
                          </div>
                          <span className="mb-2 text-success">
                            <i className="ti ti-checks fs-30" />
                          </span>
                        </div>
                      </Link>
                      <div className="d-flex">
                        <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                          <span className="text-success fs-12 d-flex align-items-center me-1">
                            <i className="ti ti-arrow-wave-right-up me-1" />
                            {mainStats?.assignedPercentage}%
                          </span>
                          of total assets
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* In Stock */}
                <div className="col-xl-3 col-md-6 px-2">
                  <div className="card">
                    <div className="card-body bg-white">
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#asset-stats-info-modal"
                        onClick={() => handleCardClick("instock-assets")}
                      >
                        <div className="border-bottom mb-3 pb-3 d-flex align-items-center justify-content-between">
                          <div>
                            <h2 className="mb-2">{mainStats?.instock}</h2>
                            <p className="fw-medium text-truncate">
                              In Stock Assets
                            </p>
                          </div>
                          <span className="mb-2 text-success">
                            <i className="ti ti-devices fs-30" />
                          </span>
                        </div>
                      </Link>
                      <div className="d-flex">
                        <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                          <span className="text-success fs-12 d-flex align-items-center me-1">
                            <i className="ti ti-arrow-wave-right-up me-1" />
                            {mainStats?.instockPercentage}%
                          </span>
                          of total assets
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lost Assets */}
                <div className="col-xl-3 col-md-6 px-2">
                  <div className="card">
                    <div className="card-body bg-white">
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#asset-stats-info-modal"
                        onClick={() => handleCardClick("lost-assets")}
                      >
                        <div className="border-bottom mb-3 pb-3 d-flex align-items-center justify-content-between">
                          <div>
                            <h2 className="mb-2">{mainStats?.lost}</h2>
                            <p className="fw-medium text-truncate">
                              Lost Assets
                            </p>
                          </div>
                          <span className="mb-2 text-warning">
                            <i className="ti ti-alert-triangle fs-30" />
                          </span>
                        </div>
                      </Link>
                      <div className="d-flex">
                        <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                          <span className="text-warning fs-12 d-flex align-items-center me-1">
                            <i className="ti ti-arrow-wave-right-up me-1" />
                            {mainStats?.lostPercentage}%
                          </span>
                          of total assets
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row gap-4">
                {/* Assets By Category/Products Chart */}
                <div className="col-md-12">
                  <AssetActivityChart
                    assets={filteredAssets}
                    assetCategories={assetCategoryOptions}
                    selectedCategoryId={categoryFilter}
                    onCategorySelect={handleChartCategorySelection}
                    onClearFilter={handleClearFilters}
                    categoryFilter={categoryFilter}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 row ">
            {/* Asset Changes Chart */}
            <div className="col-lg-6 border-0">
              <div className="card ">
                <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                  <h5 className="mb-2">
                    Asset Changes
                    <span className="fs-12 text-muted"> (this month)</span>
                  </h5>
                </div>
                <div className="card-body bg-white">
                  <div className="chartjs-wrapper-demo position-relative mb-4">
                    <Chart
                      type="doughnut"
                      data={chartData}
                      options={chartOptions}
                      className="w-full attendence-chart md:w-30rem"
                      style={{ zIndex: "10" }}
                    />

                    <div className="position-absolute text-center attendance-canvas">
                      <p className="fs-13 mb-1">Total Changes</p>
                      <h3>{assetChangesData.total}</h3>
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-evenly flex-wrap">
                    <div className="border-end text-center me-1 pe-2 mb-3">
                      <p className="fs-13 d-inline-flex align-items-center mb-1">
                        <i className="ti ti-circle-filled fs-10 me-1 text-success" />
                        Assigned
                      </p>
                      <h5>{getActivePercentage()}%</h5>
                    </div>
                    <div className="border-end text-center me-1 pe-2 mb-3">
                      <p className="fs-13 d-inline-flex align-items-center mb-1">
                        <i className="ti ti-circle-filled fs-10 me-1 text-warning" />
                        Unassigned
                      </p>
                      <h5>{getInactivePercentage()}%</h5>
                    </div>
                    <div className="text-center me-1 pe-2 mb-3">
                      <p className="fs-13 d-inline-flex align-items-center mb-1">
                        <i className="ti ti-circle-filled fs-10 me-1 text-danger" />
                        Updated
                      </p>
                      <h5>{getUpdatedPercentage()}%</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>

      {/* Modal to show on cards and number clicks  */}
      <QuickInfoModal
        title={modalInfo}
        dataSource={modalData}
        columns={modalDataColumns}
        selectedHistoryAsset={selectedHistoryAsset}
      />
    </>
  );
};

export default AssetsStocks;
